import React, {useState, useRef, useEffect} from "react";
import styled from "styled-components";
import {FaAngleLeft, FaAngleRight} from "react-icons/fa";
import useWindowTools from "../../react-hooks/useWindowTools";

const HorizontalScroll = (props) => {
  const defaultScrollFactor = 0.8;
  const {isMobile} = useWindowTools();

  const {
    children,
    showNavButtons,
    showScroll = true,
    scrollWidthFactor = defaultScrollFactor,
    padding,
    className = "",
    style
  } = props;

  const scrollableRef = useRef();
  const [scrollPosition, setScrollPosition] = useState(0);

  const getCurrentScroll = () => scrollableRef.current?.scrollLeft;
  const getScrollWidth = () => scrollableRef.current?.scrollWidth - scrollableRef.current?.clientWidth;
  const getElementWidth = () => scrollableRef.current?.clientWidth;

  const scroll = (direction = "right") => {
    const scrollWidth = getScrollWidth();
    const currentScroll = getCurrentScroll();
    let pixels = getElementWidth() * scrollWidthFactor;
    if (direction !== "right") pixels = pixels * -1;

    let newScroll = currentScroll + pixels;
    if (newScroll < 0) newScroll = 0;
    if (newScroll > scrollWidth) newScroll = scrollWidth;

    if (newScroll === currentScroll) return;

    scrollableRef.current?.scroll({
      left: newScroll,
      behavior: "smooth"
    });

    onScroll();
  };

  const onScroll = () => {
    const currentScroll = getCurrentScroll();
    setScrollPosition(currentScroll);
  };

  useEffect(() => {
    const timeout = 500;

    setTimeout(() => {
      if (scrollableRef?.current) {
        scrollableRef.current.addEventListener("scroll", onScroll);
        setScrollPosition(getCurrentScroll());
      }
    }, timeout);

    return () => {
      if (scrollableRef?.current) {
        scrollableRef.current.removeEventListener("scroll", onScroll);
      }
    };
  }, []);

  return <div
    className={className}
    style={{height: "max-content", position: `${isMobile ? null : "relative"}`, ...style}}
  >
    <HorizontalScrollWrapper showScroll={showScroll} padding={padding} className={`${showScroll ? "" : "hide-scroll"}`}>
      <div
        className={"horizontal-inner-wrapper"}
        ref={scrollableRef}
      >
        <div className="inner-content-wrapper">
          {children}
        </div>

        {showNavButtons && <NavButtons>
          <div className='nav-btn-wrapper btn-left'>
            <div
              className={`nav-btn ${scrollPosition <= 0 ? "disabled" : ""}`}
              onClick={() => scroll("left")}
            >
              <FaAngleLeft/>
            </div>
          </div>

          <div className='nav-btn-wrapper btn-right'>
            <div
              className={`nav-btn ${scrollPosition >= getScrollWidth() ? "disabled" : ""}`}
              onClick={() => scroll("right")}>
              <FaAngleRight/>
            </div>
          </div>
        </NavButtons>}
      </div>
    </HorizontalScrollWrapper>
  </div>;
};

const hidePadding = 30;
const hidePaddingExtra = 20;

const HorizontalScrollWrapper = styled.div.attrs({
  className: "farmy-horizontal-scroll overflow-hidden"
})`
  &.hide-scroll {
    padding-bottom: ${props => (props?.padding || 0) + hidePadding}px;
    
    .horizontal-inner-wrapper {
      margin-bottom: ${props => (props?.padding || 0) - hidePadding - hidePaddingExtra}px;
      padding-bottom: ${hidePadding}px;
    }
  }
  
  .horizontal-inner-wrapper {
    overflow-x: scroll;
    padding: ${props => props?.padding || "10px 0"};
  }

  .inner-content-wrapper {
    width: max-content;
  }
`;

const NavButtons = styled.div`
  .nav-btn-wrapper {
    position: absolute;
    z-index: 2;
    height: 100%;
    bottom: 0;
    display: flex;
    align-items: center;
    
    &.btn-left {
      left: -25px;
    }
    
    &.btn-right {
      right: 25px;
    }
    
    .nav-btn {
      font-size: 25px;
      background: white;
      cursor: pointer;
      height: 50px;
      width: 50px;
      position: absolute;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      box-shadow: 6px 6px 8px -2px rgba(0, 0, 0, 0.3);
      transition: all 300ms;
      
      &.disabled {
        background: #eeeeee;
        color: #777777;
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
`;

export default HorizontalScroll;
